import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAcceptInvitation } from "@/features/clients/hooks/use-clients";
import type { ClientInvitation } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { CheckCircleIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";

const AcceptInvitationButton = ({ invitationId }: { invitationId: string }) => {
  const { t } = useTranslation("companies");
  const acceptInvitation = useAcceptInvitation();

  const handleAccept = async () => {
    try {
      await acceptInvitation.mutateAsync(invitationId);
      toast.success(t("messages.invitationAccepted"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.invitationAcceptFailed"),
      );
    }
  };

  return (
    <Button
      variant="default"
      size="sm"
      onClick={handleAccept}
      disabled={acceptInvitation.isPending}
    >
      <CheckCircleIcon className="h-4 w-4 mr-2" />
      {t("actions.accept")}
    </Button>
  );
};

export const useInvitationsColumns = (): ColumnDef<ClientInvitation>[] => {
  const { t } = useTranslation("companies");

  return [
    {
      accessorKey: "client.name",
      header: t("table.company"),
      cell: ({ row }) => {
        const { client } = row.original;

        return (
          <div className="flex items-center gap-3">
            <CompanyAvatar logo={client.logo} name={client.name} size="sm" />
            <div className="font-medium">{client.name}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "invitation.role",
      header: t("table.role"),
      cell: ({ row }) => {
        const role = row.original.invitation.role;
        return <Badge variant="secondary">{role}</Badge>;
      },
    },
    {
      accessorKey: "inviterEmail",
      header: t("table.invitedBy"),
      cell: ({ row }) => {
        const inviterEmail = row.original.inviterEmail;
        return (
          <div className="text-sm text-muted-foreground">
            {inviterEmail || t("table.unknown")}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => (
        <div className="text-right">{t("companies.table.actions")}</div>
      ),
      cell: ({ row }) => {
        const { invitation } = row.original;

        return (
          <div className="flex items-center justify-end">
            <AcceptInvitationButton invitationId={invitation.id} />
          </div>
        );
      },
    },
  ];
};
